/**
 * Which watched fields must be dropped because THIS surface's own write may
 * have moved them?
 *
 * A `FieldWatch` says "the worker ran once this value stops matching the
 * baseline". That inference only holds while the baseline is a value nobody on
 * our side is about to change. Watch a field our own request moves and the very
 * first poll — about a second later, long before any automation — sees a
 * different value and declares the wave satisfied.
 *
 * `isSatisfied` is a `some`, so this is not a local mistake: ONE such watch
 * ends the wave for that guest and takes every correct sibling watch with it.
 * The surface then broadcasts a row that predates the worker and stops looking,
 * which is indistinguishable from "the automation did nothing".
 *
 * Two fields qualify on the re-decision lanes:
 *
 *  • `acceptanceState` — written directly by the request. (In practice
 *    `watchedFieldsFor` cannot emit it non-additional, because
 *    `GUEST_UPDATE_FIELD_WHITELIST` does not list it and the classifier fails
 *    toward "additional". It is named here anyway: the rule is about what the
 *    write touches, and relying on a classification detail elsewhere to keep
 *    this correct is how the branch this replaces became dead without anyone
 *    noticing.)
 *
 *  • `state` — on BOTH lanes where the server moves it. There are exactly two
 *    on the `updateGuest` path:
 *
 *      `forceWaitlist` (`GuestService.java:4022`) rewrites it to WAITLISTED,
 *      and `isRequestApproval` (`:3984`) sets it to ACCEPTED whenever an
 *      acceptance goes PENDING → ACCEPTED with no `state` in the payload.
 *
 *    The first cut of this rule covered only `forceWaitlist` — i.e. it missed
 *    the ORDINARY approval, which is the common case and the one this whole
 *    mechanism exists for. On that lane the baseline stayed the pre-write
 *    value, the first poll (~1s) saw ACCEPTED, and because `isSatisfied` is a
 *    `some` the wave declared itself satisfied on our own write and killed
 *    every correct sibling watch with it.
 *
 *    Dropped rather than re-baselined because neither rewrite is unconditional:
 *    `forceWaitlist` only fires while the capacity gate would still block, and
 *    capacity can free up between the dialog rendering and the click. There is
 *    no value to baseline against, and inventing one produces a false "the
 *    automation ran".
 *
 * Dropping loses precision, and that is the honest trade: the caller falls back
 * to a coarse pulse when nothing watchable is left. A coarse pulse refreshes
 * late; a wrong watch reports success immediately and never refreshes at all.
 */
export interface WatchableField {
  field: string;
  isAdditional: boolean;
}

export interface OwnWriteShape {
  /** The request carried `forceWaitlist`, so the server may rewrite `state`. */
  forceWaitlist?: boolean;
  /**
   * The request takes an acceptance from PENDING to ACCEPTED, so the server's
   * approval sync writes `state` (`GuestService.java:3984`).
   *
   * Deliberately COARSER than the server's own `isRequestApproval`, which also
   * requires `previous.state != WAITLISTED`. The caller's `guest` object can be
   * stale — nothing refetches it while a popup sits open (`refetchType:"none"`)
   * — so a client-side WAITLISTED check can be wrong in the direction that
   * re-opens the bug. Because `isSatisfied` is a `some`, dropping one watch too
   * many merely falls back to a coarse pulse, while keeping one too many kills
   * the whole wave. Over-dropping is the safe side, and it is the same trade
   * `forceWaitlist` already makes.
   */
  approvesPendingRequest?: boolean;
}

export function dropSelfWrittenFields<T extends WatchableField>(
  fields: readonly T[],
  ownWrite: OwnWriteShape = {},
): T[] {
  return fields.filter(({ field, isAdditional }) => {
    if (isAdditional) return true;
    if (field === "acceptanceState") return false;
    if (
      field === "state" &&
      (ownWrite.forceWaitlist === true || ownWrite.approvesPendingRequest === true)
    ) {
      return false;
    }
    return true;
  });
}
